import type { ListingsSortBy, Listing, ListingSubtype, ListingType } from 'app/types/listing';
import type { ScreenParameters } from 'app/types/navigation';
import type { User } from 'app/types/user';
import { CustomerIO } from 'customerio-reactnative';
import type * as Calendar from 'expo-calendar/legacy';
import type { MixpanelProperties } from 'mixpanel-react-native';
import { Mixpanel } from 'mixpanel-react-native';
import { env } from './env';
import { log, recordError } from './monitoring';
import type { NewAddressSource } from 'app/types/address';
import { AppEventsLogger, Settings } from 'react-native-fbsdk-next';
import type { Params } from 'react-native-fbsdk-next/lib/typescript/src/FBAppEventsLogger';
import { logoutCustomerIO } from 'app/hooks/customerIO';
import { isMobileNative } from 'app/utils/web-helpers';
import { Platform } from 'react-native';
import * as SentryMobile from '@sentry/react-native';
import * as SentryWeb from '@sentry/react';
import { getCrashlytics, setUserId as CrashlyticsSetUserId } from '@react-native-firebase/crashlytics';
import { type RecommendedListing } from 'app/types/recommender';
import { type SharerType } from 'app/types/preferences';
import { initializeGoogleTagManager, pushGoogleTagManagerEvent } from './google-tag-manager';

export enum Event {
  FestivalDateUpdated = 'festival_date_updated',
  FestivalPromptShown = 'festival_prompt_shown',
  AppTrackingEnabled = 'app_tracking_enabled',
  BrowseListings = 'browse_listings',
  CalendarAddLoanModalOpened = 'calendar_add_loan_modal_opened',
  CalendarPermissionStatus = 'calendar_permission_status',
  ChangeTab = 'change_tab',
  ChangeSharingRadius = 'change_sharing_radius',
  FavoriteListing = 'favorite_listing',
  FavoriteGrouping = 'favorite_grouping',
  ListingClosed = 'listing_closed',
  ListingOpened = 'listing_opened',
  ListingsGroupingOpened = 'listings_grouping_opened',
  LoanAddedToCalendar = 'loan_added_to_calendar',
  LoanDetailsClosed = 'loan_details_closed',
  LoanDetailsOpened = 'loan_details_opened',
  LoginSuccess = 'login_success',
  NewOpened = 'new_opened',
  NotificationPermissionRegistered = 'notification_permission_registered',
  NotificationPermissionRequested = 'notification_permission_requested',
  NotificationPermissionStatus = 'notification_permission_status',
  NotificationPermissionViewed = 'notification_permission_viewed',
  NotificationReceived = 'notification_received',
  OffreDefricheurActivated = 'offre_defricheur_activated',
  OffreDefricheurOpened = 'offre_defricheur_opened',
  OffreTrialOpened = 'offre_trial_opened',
  OffreTrialActivated = 'offre_trial_activated',
  PaywallClosed = 'paywall_closed',
  PaywallOpened = 'paywall_opened',
  PromoBabillardBannerClicked = 'promo_babillard_banner_clicked',
  PromoTrialPopupClicked = 'promo_trial_popup_clicked',
  QuickOffersOpened = 'quick_offers_opened',
  QuickOffersPosted = 'quick_offers_posted',
  OfferHubManageOffersPressed = 'offer_hub_manage_offers_pressed',
  OfferHubContinuePressed = 'offer_hub_continue_pressed',
  OfferHubFinishPressed = 'offer_hub_finish_pressed',
  MultipleOfferCardPressed = 'multiple_offer_card_pressed',
  ScreenViewed = 'screen_viewed',
  SearchMembers = 'search_members',
  SearchPerformed = 'search_performed',
  SendMessage = 'send_message',
  SendMessageToMember = 'send_message_to_member',
  SendMessageToSupport = 'send_message_to_support',
  ShareAction = 'share_action',
  SignUpSuccess = 'sign_up_success',
  TutorialStarted = 'tutorial_started',
  TutorialStep = 'tutorial_step',
  TutorialEnded = 'tutorial_ended',
  UnfavoriteListing = 'unfavorite_listing',
  UnfavoriteGrouping = 'unfavorite_grouping',
  ClosedAlert = 'closed_alert',
  ClickedSortBabillard = 'clicked_sort_babillard',
  DidSortBabillard = 'did_sort_babillard',
  AddressAdded = 'address_added',
  AddressDeleted = 'address_deleted',
  BrowseTopListings = 'browse_top_listings',
  ClickedTopListing = 'clicked_top_listing',
  BrowseSimilarListings = 'browse_similar_listings',
  ClickedSimilarListing = 'clicked_similar_listing',
  BrowseRecommendedListings = 'browse_recommended_listings',
  ClickedRecommendedListing = 'clicked_recommended_listing',
  SharedInviteCode = 'shared_invite_code',
  SubscriptionCodePopupShown = 'subscription_code_popup_shown',
  SubscriptionCodePopupAccepted = 'subscription_code_popup_accepted',
  UserInitiatedLogout = 'user_initiated_logout',
  UnexpectedLogout = 'unexpected_logout',
  ClickedPromptPhoneNumber = 'clicked_prompt_phone_number',
  SubmittedPromptPhoneNumber = 'submitted_prompt_phone_number',
  BrowseRecommendedNeeds = 'browse_recommended_needs',
  ClickedRecommendedNeed = 'clicked_recommended_need',
  AppStarted = 'app_started',
  ChooseSharerType = 'choose_sharer_type',
  ChooseTagsAsBorrower = 'choose_tags_as_borrower',
  ChooseTagsAsLender = 'choose_tags_as_lender',
  GiveUpOnboarding = 'give_up_onboarding',
  ClickedStreakFlame = 'clicked_streak_flame',
  Metrics = 'metrics',
  SocialLinkClicked = 'social_link_clicked',
}

export interface EventProperties extends MixpanelProperties {
  [Event.FestivalDateUpdated]: { source: 'signup' | 'profile' };
  [Event.FestivalPromptShown]: { kind: 'initial' | 'renewal' };
  [Event.OfferHubManageOffersPressed]: undefined;
  [Event.OfferHubContinuePressed]: undefined;
  [Event.OfferHubFinishPressed]: undefined;
  [Event.MultipleOfferCardPressed]: undefined;
  [Event.Metrics]: {
    screen_name: string;
    load_time: string;
  };
  [Event.BrowseRecommendedListings]: {
    user_id?: User['id'];
    listing_type: ListingType;
    strategy: string;
  };
  [Event.ClickedRecommendedListing]: {
    user_id?: User['id'];
    listing_id: Listing['id'];
    listing_type: Listing['type'];
    listing: Listing['item']['name'];
    predicted_score: RecommendedListing['score'];
    strategy: RecommendedListing['strategy'];
  };
  [Event.NotificationPermissionStatus]: {
    status: 'denied' | 'granted' | 'undetermined';
  };
  [Event.AppTrackingEnabled]: { granted: boolean };
  [Event.PaywallOpened]: { source: keyof ScreenParameters | 'new_need' };
  [Event.PaywallClosed]: { action: 'cancel' | 'subscribe' };
  [Event.ListingsGroupingOpened]: {
    grouping_name: string;
    listing_type: ListingType;
    listing_category: string;
  };
  [Event.ListingOpened]: {
    listing_id: string;
    listing_type?: ListingType;
    listing_category?: string;
    listing_subtype?: ListingSubtype;
    listing_service_category?: string | null;
    is_quick_offer: boolean;
  };
  [Event.ClickedPromptPhoneNumber]: {
    user_id: string;
  };
  [Event.SubmittedPromptPhoneNumber]: {
    user_id: string;
  };
  [Event.ListingClosed]: {
    listing_id: string;
    listing_type?: ListingType;
    listing_category?: string;
    listing_subtype?: ListingSubtype;
    listing_service_category?: string | null;
    is_quick_offer: boolean;
  };
  [Event.FavoriteListing]: {
    listing_id: string;
    listing_type: ListingType;
    listing_category: string;
    is_quick_offer: boolean;
  };
  [Event.FavoriteGrouping]: {
    grouping_id: string;
    listing_category?: string;
  };
  [Event.UnfavoriteListing]: {
    listing_id: string;
    listing_type?: ListingType;
    listing_category?: string;
    is_quick_offer?: boolean;
  };
  [Event.UnfavoriteGrouping]: {
    grouping_id: string;
    listing_category?: string;
  };
  [Event.SendMessage]: {
    listing_id: string;
    listing_type: ListingType;
    listing_category: string;
    listing_subtype?: ListingSubtype;
    listing_service_category?: string | null;
    is_first: 'y' | 'n';
    is_quick_offer: boolean;
  };
  [Event.SendMessageToMember]: {
    user_id: string;
    is_first: 'y' | 'n';
  };
  [Event.SendMessageToSupport]: undefined;
  [Event.LoginSuccess]: { provider: 'Facebook' | 'Google' | 'Apple' | 'Email' };
  [Event.SignUpSuccess]: {
    provider: 'Facebook' | 'Google' | 'Apple' | 'Email';
  };
  [Event.SearchPerformed]: {
    query: string;
    category?: string;
    serviceCategory?: string;
    searchDistance?: number;
    listingType?: ListingType;
    listingSubtypes?: ListingSubtype[];
    sortBy?: ListingsSortBy;
  };
  [Event.BrowseListings]: {
    listingsType: ListingType;
    search?: string;
    category?: string;
    serviceCategory?: string;
    subtypes?: string;
    groups?: string[];
    sortBy?: ListingsSortBy;
  };
  [Event.LoanAddedToCalendar]: { added: boolean };
  [Event.CalendarPermissionStatus]: { status: Calendar.PermissionStatus };
  [Event.ChangeTab]: {
    page_name: string;
    tab: string;
  };
  [Event.SearchMembers]: {
    addressIds?: string[];
    groupIds?: string[];
    search?: string;
  };
  [Event.ShareAction]: {
    action: string;
  };
  [Event.ChangeSharingRadius]: {
    radius: number;
  };
  [Event.TutorialStarted]: {
    tutorial: string;
  };
  [Event.TutorialStep]: {
    tutorial: string;
    step: number;
  };
  [Event.TutorialEnded]: {
    tutorial: string;
    completed: boolean;
  };
  [Event.ClickedSortBabillard]: {
    listingType: ListingType;
  };
  [Event.DidSortBabillard]: {
    listingType: ListingType;
    sortBy: ListingsSortBy;
  };
  [Event.AddressAdded]: {
    address: string;
    source: NewAddressSource;
  };
  [Event.AddressDeleted]: {
    address: string;
  };
  [Event.UserInitiatedLogout]: {
    userId: string;
    source: string;
  };
  [Event.UnexpectedLogout]: {
    userId: string;
    source: string;
  };
  [Event.ChooseSharerType]: {
    selected: SharerType;
  };
  [Event.ChooseTagsAsBorrower]: {
    num_selected: number;
    tags_selected: string[];
    source: 'onboarding' | 'new_listing';
  };
  [Event.ChooseTagsAsLender]: {
    num_selected: number;
    tags_selected: string[];
    source: 'onboarding' | 'new_listing';
  };
  [Event.GiveUpOnboarding]: {
    where:
      | 'present-community'
      | 'borrower-next'
      | 'lender-next'
      | 'onboarding_flow_intro'
      | 'onboarding_flow_tags'
      | 'onboarding_flow_listings';
    page_number?: number;
  };
  [Event.ClickedStreakFlame]: {
    streak_mode: 'pre_streak' | 'monthly_streak';
  };
  [Event.SocialLinkClicked]: {
    platform: 'Instagram' | 'Facebook' | 'TikTok' | 'Youtube';
  };
}

let mixpanel: Mixpanel | undefined;

initializeGoogleTagManager(env.GOOGLE_TAG_MANAGER_ID);

if (env.MIXPANEL_TOKEN) {
  const trackAutoMaticEvents = true;
  mixpanel = isMobileNative
    ? new Mixpanel(env.MIXPANEL_TOKEN, trackAutoMaticEvents)
    : new Mixpanel(env.MIXPANEL_TOKEN, trackAutoMaticEvents, false);
  void mixpanel
    .init()
    .then(() => log('Mixpanel initialized'))

    .catch((e) => {
      console.error('Failed to initialize Mixpanel', e);
      recordError(e);
    });
}

export function logout() {
  mixpanel?.reset();
  logoutCustomerIO();
}

export function identify(user: User) {
  log(`Processing 'identify' event for user with ID ${user.id}`);
  void mixpanel?.identify(user.id);
  if (globalThis.cioanalytics) {
    void globalThis.cioanalytics.identify(user.id, { ...user, platform: Platform.OS });
  } else {
    void CustomerIO.identify({ userId: user.id, traits: { ...user, platform: Platform.OS } });
  }
  if (isMobileNative) {
    void CrashlyticsSetUserId(getCrashlytics(), user.id);
    SentryMobile.setUser({ id: user.id });
  } else {
    SentryWeb.setUser({ id: user.id });
  }
}

export function flush() {
  if (isMobileNative) {
    mixpanel?.flush();
  }
}

export function screen(name: keyof ScreenParameters, properties?: MixpanelProperties) {
  track(Event.ScreenViewed, { ...properties, name });
}

export async function setIDFACollectionEnabled(enabled: boolean) {
  log(`Updating IDFA collection preference to ${enabled ? 'enabled' : 'disabled'}`);
  track(Event.AppTrackingEnabled, { granted: enabled });

  if (isMobileNative) {
    Settings.initializeSDK();
    if (enabled) {
      await Settings.setAdvertiserTrackingEnabled(true);
    }
    log(`Initialized Facebook SDK ${enabled ? 'with' : 'without'} advertiserTrackingEnabled`);
  }
}

export function track<T extends Event>(
  event: T,
  ...[properties]: undefined extends EventProperties[T] ? [] | [EventProperties[T]] : [EventProperties[T]]
) {
  // Enforce this event's privacy contract even for untyped callers.
  if (event === Event.SubmittedPromptPhoneNumber) {
    properties = { user_id: properties?.user_id } as EventProperties[T];

    // Navigation parameters are untrusted and may contain recovery credentials.
    if (event === Event.ScreenViewed) {
      properties = { name: properties?.name } as EventProperties[T];
    }
    log(`Processing '${event}' event with properties`, properties);
    mixpanel?.track(event, properties);
    pushGoogleTagManagerEvent(event, properties);
    if (globalThis.cioanalytics) {
      void globalThis.cioanalytics.track(event, properties);
    } else {
      void CustomerIO.track(event, properties);
    }
    if (isMobileNative) {
      SentryMobile.logger.debug('TrackEvent', { event, ...properties });
    } else {
      SentryWeb.logger.debug('TrackEvent', { event, ...properties });
    }
  }
}

export enum CustomFacebookEvent {
  OpenedApp = 'opened_app',
  OfferedLoan = 'offered_loan',
  AcceptedLoan = 'accepted_loan',
  Contributed = 'contributed',
}

export enum ContributionType {
  SentMessage = 'sent_message',
  PublishedOffer = 'published_offer',
  PublishedRequest = 'published_request',
  PublishedQuickOffers = 'published_quick_offers',
  Favourite = 'favourite',
}

export function trackOnFacebook(eventName: string, ...args: (number | Params)[]) {
  if (!isMobileNative) return;
  log(`Processing '${eventName}' event for Facebook with args`, args);
  AppEventsLogger.logEvent(eventName, ...args);
}

export function trackPurchaseOnFacebook(purchaseAmount: number, currencyCode: string, parameters?: Params) {
  if (!isMobileNative) return;
  log(`Processing 'purchased' event for Facebook with args`, { purchaseAmount, currencyCode, parameters });
  AppEventsLogger.logPurchase(purchaseAmount, currencyCode, parameters);
}
